import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { services } from '../app';
import type { SkyEvent } from '../astro/events';
import type { Place } from '../astro/frame';
import { keepHonorifics } from '../data/islam';
import { skyBrightnessAt } from '../astro/lightpollution';
import { usePlaceSearch } from '../live/placesearch';
import { alertState, reminderSent, toggleReminder } from '../live/push';
import { activeDay, alertable, calendarFocus, calendarPlace, computeYear, downloadEventIcs, isAsked, monthIn, phaseClock, prayerFor, remindersFor, tzOf, whenLabel } from '../live/skycalendar';
import { endPreview, panel, place, previewFrom, previewPlace, selection, setSimTime, settings, showToast } from '../state';
import { Sheet } from './chrome';
import { Icon } from './icons';
import { DayNote, EclipsePrayer } from './IslamNotes';
import { fmtHijriShort } from '../astro/hijri';
import { hijriNow, saudiStarts } from '../live/saudi';
import { CrescentPrayer } from './Crescent';

// The sky calendar: the year ahead for one place, home unless another is chosen, month by month.

type Filter = 'highlights' | 'meteor' | 'eclipse' | 'crescent' | 'moon' | 'planets' | 'all';
const FILTERS: [Filter, string][] = [
  ['highlights', 'Highlights'], ['meteor', 'Meteor showers'], ['eclipse', 'Eclipses'], ['crescent', 'New crescent'],
  ['moon', 'Moon'], ['planets', 'Planets'], ['all', 'Everything'],
];
const matches = (e: SkyEvent, f: Filter) =>
  f === 'all' ? true
  : f === 'highlights' ? e.weight >= 2
  : f === 'planets' ? e.kind === 'opposition' || e.kind === 'elongation' || e.kind === 'conjunction'
  : e.kind === f;
const near = (a: { lat: number; lon: number }, b: { lat: number; lon: number }) => Math.abs(a.lat - b.lat) < 0.05 && Math.abs(a.lon - b.lon) < 0.05;

export function CalendarPanel() {
  const s = services.value!;
  const home = previewFrom.value?.home ?? place.value;
  // Opened for another place (a reminder for it, or Tonight while previewing it): start there.
  const [other, setOther] = useState<Place | null>(() => {
    const p = calendarPlace.peek();
    calendarPlace.value = null;
    return p && !near(p, home) ? p : null;
  });
  const p = other ?? home;
  const tz = tzOf(p);
  const [otherBortle, setOtherBortle] = useState<number | null>(null);
  const bortle = other ? otherBortle : settings.value.bortle;
  const [picking, setPicking] = useState(false);
  const [q, setQ] = useState('');
  const { results, status } = usePlaceSearch(q);
  const [filter, setFilter] = useState<Filter>('highlights');
  const [events, setEvents] = useState<SkyEvent[] | null>(null);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  // The showers load just after start-up: a calendar opened from a notification waits for them.
  const [showersIn, setShowersIn] = useState(s.meteors.showers.length > 0);
  const body = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const placeCard = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!showersIn) void s.meteors.ready.then(() => setShowersIn(true));
  }, []);
  useEffect(() => {
    if (picking) input.current?.focus({ preventScroll: true });
  }, [picking]);
  // The year from the start of this hour, so a place's year is worked out once and kept.
  const from = useMemo(() => Math.floor(Date.now() / 3600e3) * 3600e3, []);

  // Another place's sky brightness, for its meteor rates.
  useEffect(() => {
    if (!other) return;
    setOtherBortle(null);
    let live = true;
    void skyBrightnessAt(other.lat, other.lon).then((b) => live && setOtherBortle(b?.bortle ?? 4));
    return () => void (live = false);
  }, [other?.lat, other?.lon]);

  // Worked out off the main thread, a month at a time; a new place or closing the calendar stops it. Saudi Arabia's
  // decided month starts move the evenings of the 29th, so a new decision works the year out again.
  const starts = saudiStarts.value;
  useEffect(() => {
    if (bortle == null || !showersIn) return;
    setEvents(null);
    setDone(false);
    setFailed(false);
    return computeYear(p, from, s.meteors.showers, bortle, (list, d) => {
      setEvents(list);
      setDone(d);
    }, () => setFailed(true));
  }, [p.lat, p.lon, bortle, showersIn, starts]);

  // Opened at one event (from "On now" or a notification): show it, open, in view.
  useEffect(() => {
    const id = calendarFocus.value;
    if (!id || !events) return;
    const e = events.find((x) => x.id === id);
    if (!e) {
      if (done || failed) calendarFocus.value = null;
      return;
    }
    if (!matches(e, filter)) setFilter('all');
    calendarFocus.value = null;
    setTimeout(() => {
      const el = document.getElementById(`ev-${id}`) as HTMLDetailsElement | null;
      if (!el) return;
      el.open = true;
      el.scrollIntoView({ block: 'center' });
    }, 60);
  }, [events, done, failed]);

  const shown = (events ?? []).filter((e) => matches(e, filter));
  const months: [string, SkyEvent[]][] = [];
  for (const e of shown) {
    const m = monthIn(e.t, tz);
    if (months[months.length - 1]?.[0] !== m) months.push([m, []]);
    months[months.length - 1][1].push(e);
  }

  const choose = (g: { name: string; country?: string; latitude: number; longitude: number; elevation?: number; timezone?: string }) => {
    const next = { lat: g.latitude, lon: g.longitude, height: g.elevation ?? 20, name: g.name, timeZone: g.timezone };
    setOther(near(next, home) ? null : next);
    setPicking(false);
    setQ('');
    body.current?.closest('.sheet-body, .sheet')?.scrollTo?.({ top: 0 });
    placeCard.current?.focus({ preventScroll: true });
  };

  // "Show me" goes to a moment the thing can be seen: an eclipse's greatest moment if that is in view, or
  // else the first stage that is.
  const showAt = (e: SkyEvent) => {
    if (e.kind !== 'eclipse' || !e.phases) return e.t;
    const seen = e.phases.filter((x) => x.up);
    return seen.find((x) => x.label === 'Greatest')?.t ?? seen[0]?.t ?? e.t;
  };
  const showMe = (e: SkyEvent) => {
    if (other) previewPlace(other, other.name, 'site');
    else if (previewFrom.peek()) endPreview();
    setSimTime(new Date(showAt(e)), 0);
    if (e.select) selection.value = e.select;
    panel.value = null;
  };

  // Set or taken away on this phone at once; called done only once the server has it (see toggleReminder). A tap
  // while the last one is still on its way is not taken as a second tap, which would undo it.
  const sending = useRef(new Set<string>());
  const remind = async (e: SkyEvent) => {
    if (sending.current.has(e.id)) return;
    sending.current.add(e.id);
    try {
      const { text, ms } = await toggleReminder(e, p, !!other);
      showToast(text, ms);
    } finally {
      sending.current.delete(e.id);
    }
  };

  return (
    <Sheet onClose={() => (panel.value = null)} size="tall" label="Sky calendar">
      <h2 class="sheet-title">Sky calendar</h2>
      <p class="sheet-lead">The year ahead in the sky, worked out for one place.</p>
      <div class="cal-place" ref={body}>
        <button class="go-card" ref={placeCard} aria-expanded={picking} onClick={() => setPicking(!picking)}>
          <span class="result-icon"><Icon name="pin" size={20} /></span>
          <span class="result-text">
            <span class="result-label">{p.name}</span>
            <span class="result-sub">{other ? 'Another place' : 'Your location'}, in its local time ({tz.replace(/_/g, ' ')}) · Change</span>
          </span>
          <Icon name="chevron" size={16} />
        </button>
        {other && !picking && (
          <button class="btn-ghost small" onClick={() => setOther(null)}><Icon name="locate" size={16} /> Back to {home.name}</button>
        )}
        {picking && (
          <div class="cal-search">
            <div class="search-box">
              <Icon name="search" size={18} />
              <input ref={input} value={q} onInput={(ev) => setQ((ev.target as HTMLInputElement).value)} placeholder="Search for a town or city" autocomplete="off" aria-label="Search for a place" />
            </div>
            {other && <button class="btn-ghost small" onClick={() => { setOther(null); setPicking(false); setQ(''); }}><Icon name="locate" size={16} /> {home.name} (your location)</button>}
            <div class="muted small pad" role="status" aria-live="polite">
              {status === 'searching' ? 'Searching…'
                : status === 'none' ? `No places found for “${q.trim()}”. Try the nearest town.`
                : status === 'failed' ? (navigator.onLine ? 'Place search is not responding. Try again in a moment.' : 'Place search needs a connection.')
                : results.length ? <span class="sr-only">{results.length === 1 ? 'One place found' : `${results.length} places found`}</span>
                : ''}
            </div>
            <div class="results">
              {results.map((g) => (
                <button class="result" key={`${g.latitude},${g.longitude}`} onClick={() => choose(g)}>
                  <span class="result-icon"><Icon name="pin" size={18} /></span>
                  <span class="result-text">
                    <span class="result-label">{g.name}</span>
                    <span class="result-sub">{[g.admin1, g.country].filter(Boolean).join(', ')}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div class="band-row wrap cal-filters" role="group" aria-label="Show">
        {FILTERS.map(([f, label]) => (
          <button key={f} class={`band-chip ${filter === f ? 'on' : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>{label}</button>
        ))}
      </div>

      {!events && <div class="empty">{failed ? `Ahillah could not work out the year for ${p.name} just now.` : `Working out the year for ${p.name}…`}</div>}
      {events && !shown.length && done && <div class="empty">Nothing of this kind in the year ahead from {p.name}.</div>}
      {months.map(([m, list]) => (
        <section key={m} class="cal-month">
          <h3 class="section-title">{m}</h3>
          {list.map((e) => {
            const asked = isAsked(e, p);
            // Set offline, a reminder waits on this phone until the server has it: say so, not "set".
            const waiting = asked && alertState.value === 'on' && !reminderSent(e, p);
            // With Sky events on, home's major events are reminded of anyway: say so rather than offer a second one. Most
            // of those come only when the sky looks clear, so one asked for, which comes whatever the weather, is offered
            // ("even if cloudy"); once asked for, it is what the button shows.
            const auto = !other && settings.value.pushEvents && alertState.value === 'on' && alertable(e);
            const ifClear = auto && remindersFor(e, p).some((r) => r.clear);
            return (
              <details key={e.id} id={`ev-${e.id}`} class={`more cal-ev event w${e.weight}`}>
                <summary>
                  <span class="result-icon"><Icon name={e.icon} size={20} /></span>
                  <span class="result-text">
                    <span class="result-label">{e.title}</span>
                    {/* Beside the date, the Hijri date at that moment here (for the crescent, the 29th it is looked for on). */}
                    <span class="result-sub tnum">{whenLabel(e, tz)} · <span class="nowrap">{fmtHijriShort(hijriNow(e.kind === 'crescent' ? e.t - 60e3 : e.t, p, starts))}</span></span>
                  </span>
                  <Icon name="chevron" size={16} />
                </summary>
                <div class="more-body">
                  {e.detail && <p class="cal-detail">{keepHonorifics(e.detail)}</p>}
                  {e.kind === 'crescent' && <CrescentPrayer />}
                  {e.kind === 'day' && e.day && <DayNote day={e.day} />}
                  {e.active && <p class="cal-line">Active from {activeDay(e.active.from)} to {activeDay(e.active.to)}.</p>}
                  {e.phases && (
                    <ul class="cal-phases">
                      {e.phases.map((ph) => (
                        <li key={ph.label}>
                          <span class="tnum">{phaseClock(ph.label, ph.t, tz)}</span> {ph.label}
                          {!ph.up && <span class="muted"> ({e.icon === 'sun' ? 'the Sun' : 'the Moon'} is below the horizon)</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {prayerFor(e) && <EclipsePrayer solar={e.icon === 'sun'} />}
                  {ifClear && !asked && (e.kind !== 'eclipse' || e.visible) && <p class="cal-line">Ahillah reminds you about this one if the sky looks clear.</p>}
                  <div class="cal-actions">
                    {e.select && (e.kind !== 'eclipse' || e.visible) && <button class="btn-ghost small" onClick={() => showMe(e)}><Icon name="eye" size={16} /> Show me</button>}
                    {(e.kind !== 'eclipse' || e.visible) && (auto && !asked && !ifClear ? (
                      <button class="btn-ghost small" aria-pressed="true" onClick={() => showToast('Ahillah reminds you about this one anyway. Change that in Settings, under Sky events.', 5000)}><Icon name="bell" size={16} /> Reminder on</button>
                    ) : (
                      <button class="btn-ghost small" aria-pressed={asked} onClick={() => void remind(e)}><Icon name="bell" size={16} /> {waiting ? 'Saved on this phone' : asked ? 'Reminder set' : ifClear ? 'Remind me even if cloudy' : 'Remind me'}</button>
                    ))}
                    <button class="btn-ghost small" onClick={() => downloadEventIcs(e, p)}><Icon name="calendar" size={16} /> Add to calendar</button>
                  </div>
                </div>
              </details>
            );
          })}
        </section>
      ))}
      {events && !done && <div class="empty">{failed ? 'Ahillah could not work out the rest of the year just now.' : 'Working out the months after…'}</div>}
    </Sheet>
  );
}
